<template>
    <div>
        <span v-if="loading">Loading...</span>
        <div class="d-flex">
            <div class="flex-grow-1">
                <span>
                    <span v-if="summary.Point">{{ summary.Point }} points</span>
                    <span v-if="summary.Point && summary.Polygon"> / </span>
                    <span v-if="summary.Polygon">{{ summary.Polygon }} zones</span>
                </span>
            </div>
            <div class="px-3">
                <label>State: {{ state }}</label>
            </div>
            <div class="px-3">
                <input id="checkbox" v-model="show" type="checkbox">
                <label for="checkbox">Show Zones</label>
            </div>
            <div class="px-3">
                <input id="checkboxTooltip" v-model="enableTooltip" type="checkbox">
                <label for="checkboxTooltip">Show Tooltip</label>
            </div>
        </div>
        <l-map ref="map" :zoom="zoom" :center="center" style="height: 600px; width: 100%" @ready="onMapReady">
            <l-tile-layer :url="url" :attribution="attribution" />
            <l-geo-json :key="renderMap" v-if="show" :geojson="geojson" :options="options"
                :options-style="styleFunction" />
        </l-map>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { LMap, LTileLayer, LGeoJson } from '@vue-leaflet/vue-leaflet'
import * as L from "leaflet"
import { useEotlCore } from '../../store/eotlStore'
import 'leaflet/dist/leaflet.css'
import { getCustomIcon, jumpToLayerAndOpenPopup, focusOnPoint } from './utils'

const eotlCoreStore = useEotlCore()
const { activeMapItemByAddress } = storeToRefs(eotlCoreStore)

const props = defineProps({
    state: String,
    geojson: Object,
    config: Object
})

// Note: Will this mapboxtoken be used?
const mapboxToken = ref(eotlCoreStore?.config?.mapboxToken)
const loading = ref(false)
const show = ref(true)
const enableTooltip = ref(true)
const zoom = ref(10)
const center = ref([52.5048, 13.3940])
const fillColor = ref("#e4ce7f")
const url = ref('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png')
const attribution = ref('&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors')
const renderMap = ref(0)
const map = ref(null)

/**
 * Custom functions
 */

const onMapReady = (mapInstance) => {
    //console.dir('mapInstance //> ', mapInstance)
    map.value = mapInstance
}

/**
 * Computed
 */

const summary = computed(() => {
    let points = { Polygon: 0, Point: 0, LineString: 0 }
    if (props?.geojson?.features != undefined) {
        for (let x in props.geojson.features) {
            points[props.geojson.features[x].geometry.type]++
        }
    }
    return points
})

const options = computed(() => ({
    onEachFeature: onEachFeatureFunction.value
}))

const styleFunction = computed(() => () => ({
    weight: 1,
    color: "#333333",
    opacity: 1,
    fillColor: fillColor.value,
    fillOpacity: .25
}))

const onEachFeatureFunction = computed(() => {
    return (feature, layer) => {
        // Add icon to type
        if (feature.geometry?.type === 'Point' && ['riders', 'suppliers'].includes(feature?.properties?.groupType)) {
            const icon = getCustomIcon(L, feature)
            layer.setIcon(icon)
        }

        let title, detail = 'Unknown'
        if (feature.properties.abbr != undefined) {
            title = feature.properties.abbr
            detail = feature.properties.name
        } else {
            title = feature.properties.name
            detail = feature.properties.address
        }

        const locationDescription = "<h5 class='mb-1'>" + title + "</h4>" +
            "<div>" + detail + "</div>"

        if (feature.geometry?.type === 'Point') {
            layer.bindPopup(locationDescription)
        }

        if (enableTooltip.value) {
            layer.bindTooltip(locationDescription,
                { permanent: false, sticky: false }
            )
        }

        // Add onclick event
        layer.on('click', function (e) {
            console.log('feature.properties?.address click //> ', feature.properties?.address)
            eotlCoreStore.$patch((state) => {
                state.activeMapItemByAddress = feature.properties?.address
            })
            console.log('eotlCoreStore.activeMapItemByAddress //> ', eotlCoreStore.activeMapItemByAddress)

            focusOnPoint(e)
        })
    }
})

/**
 * Watch
 */

watch(() => [props.geojson, enableTooltip], () => {
    renderMap.value = renderMap.value + 1
}, { deep: true }) // Deep watch to detect nested changes

watch(activeMapItemByAddress, (newValue, oldValue) => {
    if (newValue && (newValue !== oldValue)) {
        jumpToLayerAndOpenPopup(map, newValue)
    }
})

</script>

<style>
.leaflet-popup-content-wrapper {
    background: #fff;
    color: var(--color-text);
    border-radius: 0px;
    border: 2px solid var(--color-text);
    font-family: RobotoMono-Regular, Roboto;
    line-height: 1.5rem;
    text-transform: uppercase;
}

.leaflet-popup-content-wrapper a {
    color: var(--color-link);
}

.leaflet-popup-content {
    margin: 10px 15px;
}

.leaflet-popup-tip-container {
    width: 30px;
    height: 15px;
}

.leaflet-popup-tip {
    border-left: 15px solid transparent;
    border-right: 15px solid transparent;
    border-top: 15px solid var(--color-text);
}
</style>
